/**
 * When the section leader guide's student summaries are written, shared by
 * the pipeline that writes them (`apps/functions/src/guideSummaries`) and the
 * guide that explains the timing.
 *
 * Each section week gets scheduled runs at fixed offsets before that
 * section's meeting, plus on-demand refreshes a section leader can ask for,
 * limited by {@link ON_DEMAND_MIN_INTERVAL_MS} and
 * {@link ON_DEMAND_MAX_PER_WEEK}.
 */

import {z} from 'zod';

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * How long before the meeting each scheduled run happens, earliest first.
 * Each sits an hour before the time the guide's copy promises, so the hourly
 * scheduler and the run itself finish by then.
 */
export const GUIDE_SUMMARY_RUN_OFFSETS_MS: readonly number[] = [
  25 * ONE_HOUR_MS,
  4 * ONE_HOUR_MS,
];

/** Shortest gap between two on-demand refreshes of one section. */
export const ON_DEMAND_MIN_INTERVAL_MS = 6 * ONE_HOUR_MS;

/** On-demand refreshes one section may ask for in one section week. */
export const ON_DEMAND_MAX_PER_WEEK = 3;

/** Callable name of the on-demand refresh. */
export const REFRESH_GUIDE_SUMMARIES_FUNCTION = 'refreshguidesummaries';

/** Request to the on-demand refresh callable. */
export const RefreshGuideSummariesRequestSchema = z.object({
  courseId: z.string().min(1),
  sectionId: z.string().min(1),
  sectionWeek: z.number().int().positive(),
});

/** Request to the on-demand refresh callable. */
export type RefreshGuideSummariesRequest = z.infer<
  typeof RefreshGuideSummariesRequestSchema
>;

/** Response from the on-demand refresh callable. */
export interface RefreshGuideSummariesResponse {
  /** Students queued for a new summary. */
  studentsQueued: number;
  /** Refreshes left this week after this one. */
  remainingThisWeek: number;
  /** Earliest time the next refresh is allowed, epoch ms. */
  nextAllowedAtMs: number;
}

/**
 * The on-demand log for one section week, at
 * `summaryPipeline/{courseId}/onDemand/{sectionId}/weeks/{week}`.
 */
export const OnDemandLogSchema = z.object({
  /** When each accepted refresh was requested, epoch ms, oldest first. */
  requestedAtMs: z.array(z.number()).default([]),
});

/** The on-demand log for one section week. */
export type OnDemandLog = z.infer<typeof OnDemandLogSchema>;

/**
 * Firestore path of a section week's on-demand log.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @param params.sectionWeek 1-indexed section week.
 * @returns The document path.
 * @throws Never.
 */
export function getOnDemandLogPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: string;
  sectionId: string;
  sectionWeek: number;
}): string {
  return `summaryPipeline/${courseId}/onDemand/${sectionId}/weeks/${String(sectionWeek)}`;
}

/**
 * The scheduled run times for a meeting, earliest first.
 *
 * @param meetingMs The section's meeting that week, epoch ms.
 * @returns Run instants in epoch ms.
 * @throws Never.
 */
export function getScheduledRunTimesMs(meetingMs: number): number[] {
  return GUIDE_SUMMARY_RUN_OFFSETS_MS.map(offset => meetingMs - offset);
}

/**
 * The scheduled run `nowMs` falls in. Run i covers the half-open interval
 * `[runTimes[i], runTimes[i+1])`, and the last run covers
 * `[runTimes[last], meetingMs)`.
 *
 * @param params.meetingMs The section's meeting that week, epoch ms.
 * @param params.nowMs The instant to place.
 * @returns The run's index and start, or null before the first run or once
 *   the section has met.
 * @throws Never.
 */
export function getCurrentScheduledRun({
  meetingMs,
  nowMs,
}: {
  meetingMs: number;
  nowMs: number;
}): {index: number; startMs: number} | null {
  if (nowMs >= meetingMs) {
    return null;
  }
  const runTimes = getScheduledRunTimesMs(meetingMs);
  for (let index = runTimes.length - 1; index >= 0; index--) {
    if (nowMs >= runTimes[index]) {
      return {index, startMs: runTimes[index]};
    }
  }
  return null;
}

/**
 * The next scheduled run strictly after `nowMs`.
 *
 * @param params.meetingMs The section's meeting that week, epoch ms.
 * @param params.nowMs The instant to place.
 * @returns The run instant in epoch ms, or null when none is left.
 * @throws Never.
 */
export function getNextScheduledRunMs({
  meetingMs,
  nowMs,
}: {
  meetingMs: number;
  nowMs: number;
}): number | null {
  return getScheduledRunTimesMs(meetingMs).find(t => t > nowMs) ?? null;
}

/** Whether an on-demand refresh is allowed, and when the next one is. */
export type OnDemandAvailability =
  | {kind: 'available'; remainingThisWeek: number}
  | {kind: 'tooSoon'; nextAllowedAtMs: number; remainingThisWeek: number}
  | {kind: 'usedUp'};

/**
 * Whether a section may ask for an on-demand refresh at `nowMs`. The gap
 * check is inclusive: a request exactly {@link ON_DEMAND_MIN_INTERVAL_MS}
 * after the last one is allowed.
 *
 * @param params.log The section week's on-demand log.
 * @param params.nowMs The instant of the request.
 * @returns The availability.
 * @throws Never.
 */
export function getOnDemandAvailability({
  log,
  nowMs,
}: {
  log: OnDemandLog;
  nowMs: number;
}): OnDemandAvailability {
  const used = log.requestedAtMs.length;
  if (used >= ON_DEMAND_MAX_PER_WEEK) {
    return {kind: 'usedUp'};
  }
  const remainingThisWeek = ON_DEMAND_MAX_PER_WEEK - used;
  const lastMs = log.requestedAtMs.at(-1);
  if (lastMs !== undefined && nowMs - lastMs < ON_DEMAND_MIN_INTERVAL_MS) {
    return {
      kind: 'tooSoon',
      nextAllowedAtMs: lastMs + ON_DEMAND_MIN_INTERVAL_MS,
      remainingThisWeek,
    };
  }
  return {kind: 'available', remainingThisWeek};
}
